import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Company, CreateCompanyDTO, User } from 'src/app/user/model/user';
import { environment } from 'src/environments/environment';
import {
  MailingListFilters,
  MailingListResponse,
} from '../models/mailing-list.model';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  http = inject(HttpClient);

  getCompanyList(): Observable<Company[]> {
    return this.http.get<Company[]>(`${environment.apiUrl}/company`);
  }
  getCompanyById(id: string): Observable<Company> {
    return this.http.get<Company>(`${environment.apiUrl}/company/${id}`);
  }

  updateCompany(id: string, company: CreateCompanyDTO): Observable<Company> {
    if (!company.id) company.id = id;
    console.log('sending company data', company);
    return this.http.put<Company>(
      `${environment.apiUrl}/company/${id}`,
      company,
    );
  }

  createCompany(company: CreateCompanyDTO): Observable<Company> {
    return this.http.post<Company>(`${environment.apiUrl}/company`, company);
  }

  uploadLogo(
    id: string,
    logo: Blob,
  ): Observable<{ success: boolean; logoUrl: string }> {
    const formData = new FormData();
    formData.append('image', logo, 'logo.png');
    return this.http.post<{ success: boolean; logoUrl: string }>(
      `${environment.apiUrl}/company/${id}/logo`,
      formData,
    );
  }
  getUserList(): Observable<User[]> {
    return this.http.get<User[]>(`${environment.apiUrl}/company-users`);
  }

  delete(id: string) {
    return this.http.delete(`${environment.apiUrl}/company/${id}`);
  }

  getDeletedCompanies(): Observable<Company[]> {
    return this.http.get<Company[]>(
      `${environment.apiUrl}/admin/companies/deleted`,
    );
  }

  activate(id: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/admin/company/${id}/activate`,
      {},
    );
  }

  setDiscoveryLimit(companyId: string, limit: number): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}/company/${companyId}/discovery-limit`,
      { limit },
    );
  }

  setRestrictedProjects(
    companyId: string,
    hasRestrictedProjects: boolean,
  ): Observable<{ data: Company }> {
    return this.http.put<{ data: Company }>(
      `${environment.apiUrl}/company/${companyId}/restricted-projects`,
      { hasRestrictedProjects },
    );
  }

  /**
   * Get filtered mailing list for marketing purposes (admin only)
   */
  getMailingList(filters: MailingListFilters): Observable<MailingListResponse> {
    let params = new HttpParams();

    if (filters.languages?.length) {
      filters.languages.forEach((lang) => {
        params = params.append('languages[]', lang);
      });
    }
    if (filters.packs?.length) {
      filters.packs.forEach((pack) => {
        params = params.append('packs[]', pack);
      });
    }
    if (filters.registeredAfter) {
      params = params.set(
        'registeredAfter',
        filters.registeredAfter.toISOString(),
      );
    }
    if (filters.registeredBefore) {
      params = params.set(
        'registeredBefore',
        filters.registeredBefore.toISOString(),
      );
    }
    if (filters.lastLoginAfter) {
      params = params.set(
        'lastLoginAfter',
        filters.lastLoginAfter.toISOString(),
      );
    }
    if (filters.lastLoginBefore) {
      params = params.set(
        'lastLoginBefore',
        filters.lastLoginBefore.toISOString(),
      );
    }
    if (filters.includeOptedOut) {
      params = params.set('includeOptedOut', 'true');
    }
    if (filters.adminsOnly) {
      params = params.set('adminsOnly', 'true');
    }

    return this.http.get<MailingListResponse>(
      `${environment.apiUrl}/admin/mailing-list`,
      { params },
    );
  }
}
